import { AvailablePlanExtra } from '../../types/AvailablePlanExtra'
import { PlanExtra } from '@dtx-company/true-common/src/types/Plan'
import { SubscriptionServiceAvailablePlanExtra } from './types/models/SubscriptionServiceAvailablePlanExtra'
import { SubscriptionServiceGetAvailablePlanExtrasJSON } from './types/jsons/SubscriptionServiceGetAvailablePlanExtrasJSON'
import { fetchSubscriptionServiceUrl } from './fetchSubscriptionServiceUrl'
import { getPlans } from './getPlans'
import { logger } from '@dtx-company/logger'
import uniqBy from 'lodash/uniqBy'

function getAvailablePlanExtrasUrl(availablePlanId: string): string {
  return `/v2/available-plans/${encodeURIComponent(availablePlanId)}/available-plan-extras`
}

function findPlanExtra(planExtras: PlanExtra[] | undefined, id: string): PlanExtra | undefined {
  if (!planExtras) return
  const matchingPlanExtra = planExtras.find(planExtra => planExtra.planExtraId === id)
  if (!matchingPlanExtra) {
    // TODO: add custom metadata for model and relationship name once logger supports it
    logger.error({
      name: 'Missing Relationship',
      message: `Unable to find a planExtra with id ${id}`,
      team: 'monetization'
    })
  }
  return matchingPlanExtra
}

function buildAvailablePlanExtrasFromJSON(
  jsonExtras: SubscriptionServiceAvailablePlanExtra[],
  {
    options,
    planExtras
  }: {
    options: GetAvailablePlanExtrasOptions
    planExtras: PlanExtra[] | undefined
  }
): AvailablePlanExtra[] {
  return jsonExtras.map(jsonExtra => {
    const planExtra = findPlanExtra(planExtras, jsonExtra.planExtraId)
    return {
      id: jsonExtra.availablePlanExtraId,
      availablePlanId: options.availablePlanId,
      planExtraId: jsonExtra.planExtraId,
      minQuantity: jsonExtra.minQuantity,
      maxQuantity: jsonExtra.maxQuantity,
      planExtra
    }
  })
}

export interface GetAvailablePlanExtrasOptions {
  availablePlanId: string
  token?: string
  include?: {
    planExtra?: boolean
  }
}

/**
 * Calls the subscription service to get AvailablePlanExtra models.
 */
export async function getAvailablePlanExtras(
  options: GetAvailablePlanExtrasOptions
): Promise<AvailablePlanExtra[]> {
  const url = getAvailablePlanExtrasUrl(options.availablePlanId)
  const response = await fetchSubscriptionServiceUrl<SubscriptionServiceGetAvailablePlanExtrasJSON>(
    {
      token: options.token,
      url: url
    }
  )

  if (!response.ok) {
    throw new Error(`Received http status ${response.status} from ${url} endpoint`)
  }

  if (!response.json) {
    throw new Error(`The ${url} endpoint did not return a json payload`)
  }

  // There's no endpoint to get planExtras directly, so query for all plans
  // and then filter to their planExtras.
  // In the future, would want to query /plan-extras?id=1,2,3 for each id we
  // need rather than getting all of them.
  const plans = options.include?.planExtra ? await getPlans({}) : undefined
  const planExtras = plans
    ? uniqBy(
        plans.reduce<PlanExtra[]>((extras, plan) => {
          return [...extras, ...(plan.planExtras || [])]
        }, []),
        'planExtraId'
      )
    : undefined

  const availablePlanExtras = buildAvailablePlanExtrasFromJSON(
    response.json.availablePlanExtras || [],
    {
      options,
      planExtras
    }
  )
  return availablePlanExtras
}
